"use client";

import type { ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type FlowFooterDensity = "comfortable" | "compact";

type FlowFooterProps = {
  onBack?: () => void;
  backDisabled?: boolean;
  primaryLabel: string;
  onPrimary: () => void;
  primaryVariant?: "default" | "outline" | "secondary";
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  primaryClassName?: string;
  primaryFullWidth?: boolean;
  secondaryAction?: ReactNode;
  hideBack?: boolean;
  backLabel?: string;
  hidePrimary?: boolean;
  statusHint?: string;
  /**
   * comfortable = large outline back + primary (onboarding).
   * compact = ghost back + default primary (eSign prepare).
   */
  density?: FlowFooterDensity;
};

export function FlowFooter({
  onBack,
  backDisabled,
  primaryLabel,
  onPrimary,
  primaryVariant = "default",
  primaryDisabled,
  primaryLoading,
  primaryClassName,
  primaryFullWidth = false,
  secondaryAction,
  hideBack,
  backLabel = "Back",
  hidePrimary = false,
  statusHint,
  density = "comfortable",
}: FlowFooterProps) {
  const compact = density === "compact";

  return (
    <div className="flex w-full items-center justify-between gap-3">
      {!hideBack && onBack ? (
        <Button
          type="button"
          variant={compact ? "ghost" : "outline"}
          onClick={onBack}
          disabled={backDisabled}
          className={cn(
            compact
              ? "px-2"
              : primaryFullWidth
                ? "h-12 min-w-20 shrink-0"
                : "h-12 min-w-20 flex-1 md:w-44 md:flex-none",
          )}
        >
          {backLabel}
        </Button>
      ) : !primaryFullWidth ? (
        <div />
      ) : null}
      {statusHint ? (
        <p className="hidden text-xs text-muted-foreground sm:block">
          {statusHint}
        </p>
      ) : null}
      <div
        className={cn(
          "ml-auto flex gap-2",
          primaryFullWidth && "flex-1 sm:flex-none",
        )}
      >
        {secondaryAction}
        {!hidePrimary ? (
          <Button
            type="button"
            variant={primaryVariant}
            onClick={onPrimary}
            disabled={primaryDisabled || primaryLoading}
            className={
              primaryClassName ??
              (compact
                ? undefined
                : primaryFullWidth
                  ? "h-12 w-full sm:w-44"
                  : "h-12 min-w-20 flex-1 md:w-44 md:flex-none")
            }
          >
            {primaryLoading ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : null}
            {primaryLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
